import type { CampaignCreationCapabilities, SponsoredBrandsCreationSubmit } from "@adpilot/shared";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { AdFormatPicker } from "@/components/campaign-creation/sponsored-brands/AdFormatPicker";
import { BiddingStrategyFields } from "@/components/campaign-creation/sponsored-brands/BiddingStrategyFields";
import { CampaignDetailsFields } from "@/components/campaign-creation/sponsored-brands/CampaignDetailsFields";
import { CreativeSection } from "@/components/campaign-creation/sponsored-brands/CreativeSection";
import {
  ChoiceRadioCard,
  ChoiceRadioGroup,
  TextField,
} from "@/components/campaign-creation/sponsored-brands/FormFields";
import { KeywordTargeting } from "@/components/campaign-creation/sponsored-brands/KeywordTargeting";
import { LandingPageFields } from "@/components/campaign-creation/sponsored-brands/LandingPageFields";
import { ProductsSection } from "@/components/campaign-creation/sponsored-brands/ProductsSection";
import { ProductTargeting } from "@/components/campaign-creation/sponsored-brands/ProductTargeting";
import { SectionCard } from "@/components/campaign-creation/sponsored-brands/SectionLayout";
import type {
  SponsoredBrandsMoneyCapabilities,
  TargetingType,
} from "@/components/campaign-creation/sponsored-brands/sponsored-brands-model";
import { useSponsoredBrandsForm } from "@/components/campaign-creation/sponsored-brands/use-sponsored-brands-form";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";

export function SponsoredBrandsCampaignForm({
  shopId,
  requestId,
  capabilities,
  isSubmitting,
  onDirtyChange,
  onCancel,
  onSubmit,
}: {
  shopId: string;
  requestId: string;
  capabilities: CampaignCreationCapabilities;
  isSubmitting: boolean;
  onDirtyChange: (dirty: boolean) => void;
  onCancel: () => void;
  onSubmit: (payload: SponsoredBrandsCreationSubmit) => void;
}) {
  const { t } = useTranslation("campaignCreation");
  const money: SponsoredBrandsMoneyCapabilities = {
    currencyCode: capabilities.currencyCode,
    dailyBudget: capabilities.dailyBudget,
    bid: capabilities.bid,
  };
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);
  const markDirty = useCallback(() => onDirtyChange(true), [onDirtyChange]);
  const form = useSponsoredBrandsForm({ shopId, requestId, capabilities, onSubmit });
  const { details, discovery, targeting, fieldError } = form;

  return (
    <div
      className="flex flex-col gap-3"
      onChangeCapture={markDirty}
      onClickCapture={(event) => {
        const target = event.target as HTMLElement;
        if (target.closest("button,[role=radio],[role=switch],[role=checkbox],[role=option]")) {
          markDirty();
        }
      }}
    >
      {discovery.isError ? (
        <Alert variant="error">
          <IconAlertTriangle />
          <AlertTitle>{t("sb.blockers.discoveryTitle")}</AlertTitle>
          <AlertDescription>{t("sb.blockers.discoveryDescription")}</AlertDescription>
        </Alert>
      ) : null}

      <SectionCard title={t("sb.campaign.title")}>
        <CampaignDetailsFields
          capabilities={capabilities}
          campaignName={details.campaignName}
          onCampaignNameChange={form.setCampaignName}
          startDate={details.startDate}
          onStartDateChange={form.setStartDate}
          endDate={details.endDate}
          onEndDateChange={form.setEndDate}
          budget={details.budget}
          onBudgetChange={form.setBudget}
          brandEntityId={details.brandEntityId}
          onBrandChange={form.selectBrand}
          brands={discovery.data?.brands ?? []}
          discoveryLoading={discovery.isLoading}
          discoveryFailed={discovery.isError}
          errors={{
            name: fieldError("campaign.name"),
            startDate: fieldError("campaign.startDate"),
            dailyBudget: fieldError("campaign.dailyBudget"),
            brandEntityId: fieldError("campaign.brandEntityId"),
          }}
        />
        <BiddingStrategyFields
          automatedBidding={form.automatedBidding}
          onAutomatedBiddingChange={form.setAutomatedBidding}
          placementAdjustmentsEnabled={form.placementAdjustmentsEnabled}
          onPlacementAdjustmentsEnabledChange={form.setPlacementAdjustmentsEnabled}
          placementValues={form.placementValues}
          onPlacementValueChange={form.setPlacementValue}
        />
      </SectionCard>

      <SectionCard title={t("sb.adGroup.title")}>
        <div className="max-w-[462px]">
          <TextField
            name="adGroupName"
            label={t("sb.adGroup.name")}
            value={details.adGroupName}
            onChange={form.setAdGroupName}
            error={fieldError("adGroup.name")}
            required
          />
        </div>
      </SectionCard>

      <SectionCard title={t("sb.format.title")}>
        <AdFormatPicker adFormat={form.adFormat} onSelect={form.selectAdFormat} />
      </SectionCard>

      <SectionCard title={t("sb.landing.title")}>
        <LandingPageFields
          adFormat={form.adFormat}
          landingPage={form.landingPage}
          stores={form.stores}
          selectedStore={form.selectedStore}
          storeEntityId={form.storeEntityId}
          storePageUrl={form.storePageUrl}
          storesLoading={discovery.isLoading}
          onStoreChange={form.selectStore}
          onStorePageChange={form.selectStorePage}
          onSpotlightStoreChange={form.selectSpotlightStore}
          onVideoLandingPageChange={form.selectVideoLandingPage}
          onProductCollectionLandingPageChange={form.selectProductCollectionLandingPage}
        />
      </SectionCard>

      {form.showsProducts ? (
        <ProductsSection
          shopId={shopId}
          maximum={form.adFormat === "video" ? 1 : 100}
          minimum={form.adFormat === "video" ? 1 : 3}
          input={form.productInput}
          onInputChange={form.setProductInput}
          asins={form.advertisedAsins}
          onRemove={form.removeAdvertisedAsin}
          onAdd={() => form.addAdvertisedProducts()}
          onAddAsins={(asins) => form.addAdvertisedProducts(asins.join("\n"))}
          isLoading={form.eligibilityPending}
        />
      ) : null}
      {form.showsCreative ? (
        <CreativeSection
          adFormat={form.adFormat}
          requiresBrandCreative={form.adFormat !== "video" || form.landingPage === "amazonStore"}
          adName={details.adName}
          onAdNameChange={form.setAdName}
          errors={{
            adName: fieldError("ad.name"),
            brandName: fieldError("ad.creative.brandName", "ad.creative.brand.brandName"),
            headline: fieldError("ad.creative.headline", "ad.creative.brand.headline"),
            logo: fieldError("ad.creative.brandLogoAssetId", "ad.creative.brand.brandLogoAssetId"),
            customImage: fieldError("ad.creative.customImageAssetId"),
            video: fieldError("ad.creative.videoAssetId"),
            products: fieldError("ad.products"),
          }}
          brandName={details.brandName}
          onBrandNameChange={form.setBrandName}
          headline={details.headline}
          onHeadlineChange={form.setHeadline}
          logoAssets={form.logoAssets}
          logoAssetId={details.logoAssetId}
          onLogoAssetChange={form.setLogoAssetId}
          customImageAssets={form.customImageAssets}
          customImageAssetId={details.customImageAssetId}
          onCustomImageAssetChange={form.setCustomImageAssetId}
          advertisedAsins={form.creativeProductAsins}
          creativeAsins={form.creativeAsins}
          onCreativeAsinsChange={form.setCreativeAsins}
          selectedStore={form.selectedStore}
          spotlightPageIds={form.spotlightPageIds}
          onSpotlightPageIdsChange={form.setSpotlightPageIds}
          spotlightPageAsins={form.spotlightPageAsins}
          onSpotlightPageAsinsChange={form.setSpotlightPageAsins}
          videoAssets={form.videoAssets}
          videoAssetId={details.videoAssetId}
          onVideoAssetChange={form.setVideoAssetId}
        />
      ) : null}

      <SectionCard title={t("sb.targeting.title")}>
        <ChoiceRadioGroup
          value={targeting.targetingType}
          onValueChange={(value) => targeting.setTargetingType(value as TargetingType)}
        >
          <ChoiceRadioCard
            value="keyword"
            label={t("sb.targeting.keyword")}
            description={t("sb.targeting.keywordDescription")}
          />
          <ChoiceRadioCard
            value="product"
            label={t("sb.targeting.product")}
            description={t("sb.targeting.productDescription")}
          />
        </ChoiceRadioGroup>
        {targeting.targetingType === "keyword" ? (
          <KeywordTargeting
            money={money}
            activeTab={targeting.keywordTab}
            onTabChange={targeting.setKeywordTab}
            input={targeting.keywordInput}
            onInputChange={targeting.setKeywordInput}
            rows={targeting.keywordRows}
            onRowsChange={targeting.setKeywordRows}
            matchTypes={targeting.matchTypes}
            onMatchTypesChange={targeting.setMatchTypes}
            bid={targeting.keywordBid}
            onBidChange={targeting.setKeywordBid}
            onAdd={targeting.addKeywords}
            negativeInput={targeting.negativeInput}
            onNegativeInputChange={targeting.setNegativeInput}
            negativeKeywords={targeting.negativeKeywords}
            onNegativeKeywordsChange={targeting.setNegativeKeywords}
            addResult={targeting.keywordAddResult}
            suggestions={targeting.keywordSuggestions}
            onAddSuggestions={targeting.addKeywordSuggestions}
            suggestionsLoading={targeting.keywordSuggestionsLoading}
          />
        ) : (
          <ProductTargeting
            money={money}
            input={targeting.productTargetInput}
            onInputChange={targeting.setProductTargetInput}
            asins={targeting.productTargetAsins}
            onAsinsChange={targeting.setProductTargetAsins}
            advertisedAsins={form.recommendationAsins}
            categories={targeting.categoryTargets}
            onCategoriesChange={targeting.setCategoryTargets}
            categorySuggestions={targeting.categorySuggestions}
            productSuggestions={targeting.productSuggestions}
            onLoadRecommendations={targeting.loadRecommendations}
            recommendationsLoading={targeting.recommendationsLoading}
            bid={targeting.productTargetBid}
            onBidChange={targeting.setProductTargetBid}
            negativeInput={targeting.negativeProductInput}
            onNegativeInputChange={targeting.setNegativeProductInput}
            negativeAsins={targeting.negativeProductAsins}
            onNegativeAsinsChange={targeting.setNegativeProductAsins}
          />
        )}
      </SectionCard>

      <footer className="flex flex-col items-center gap-3 rounded-lg border border-border bg-card p-5">
        {form.hasValidationErrors ? (
          <p role="alert" className="text-sm text-destructive-foreground">
            {t("sb.validation.fixFields")}
          </p>
        ) : null}
        <div className="flex justify-center gap-4">
          <Button type="button" variant="outline" className="w-44" onClick={onCancel}>
            {t("sb.actions.cancel")}
          </Button>
          <Button type="button" className="w-44" disabled={isSubmitting} onClick={form.submit}>
            {isSubmitting ? t("sb.actions.saving") : t("sb.actions.save")}
          </Button>
        </div>
      </footer>
    </div>
  );
}
